import { useState } from 'react'
import { Link } from '@inertiajs/react'
import SeoHead from '../../components/SeoHead'
import MarketingFooter from '../../components/MarketingFooter'

const SIGNUP_HREF = '/users/sign_up?plan=solo&src=when-spreadsheets-break'

const BREAKS = [
  {
    id: 'tax-season',
    n: 1,
    title: 'Tax season.',
    body:
      "It's January 31st and you owe every borrower a 1098. You can't remember which formula calculates interest paid YTD.",
    cta: 'Solve this in LendSolo',
    href: SIGNUP_HREF,
  },
  {
    id: 'missed-payment',
    n: 2,
    title: 'The forgotten payment.',
    body:
      "A borrower's payment didn't come through. You don't know until you check the bank manually 11 days later.",
    cta: 'Solve this in LendSolo',
    href: SIGNUP_HREF,
  },
  {
    id: 'statement-request',
    n: 3,
    title: 'The borrower statement request.',
    body:
      'Your borrower asks for a payoff quote on Sunday night. They need it by Monday morning.',
    cta: 'Solve this in LendSolo',
    href: SIGNUP_HREF,
  },
  {
    id: 'third-loan',
    n: 4,
    title: 'The third loan.',
    body: 'One spreadsheet was fine. Three is a tab problem. Five is a real problem.',
    cta: 'Solve this in LendSolo',
    href: SIGNUP_HREF,
  },
  {
    id: 'investor',
    n: 5,
    title: 'The investor.',
    body:
      'Someone wants to put $50K alongside you on a deal. Now you need to track fractional ownership and split distributions every month. Spreadsheets give up here.',
    cta: 'Solve this in LendSolo',
    href: '/users/sign_up?plan=fund&src=when-spreadsheets-break',
  },
]

const OBJECTIONS = [
  {
    title: 'CSV import.',
    body: 'One file, one upload, every loan in.',
  },
  {
    title: 'Migrate at your pace.',
    body:
      "You don't have to migrate everything day one. Add active loans first, historical loans whenever.",
  },
  {
    title: '14-day trial.',
    body:
      "If it's worse than your spreadsheet after 14 days, cancel. No charge.",
  },
]

const FEATURES = [
  {
    title: 'A dashboard, not a tab soup.',
    body:
      'Every loan, every payment, every overdue flag — one screen. No lookup formulas, no broken references.',
  },
  {
    title: 'Reminders you never have to set.',
    body:
      'Borrowers get a nudge three days before every payment. You get an alert the day one is missed.',
  },
  {
    title: 'Tax-ready, not tax-panicked.',
    body:
      'LendSolo auto-generates every 1098 for every borrower who paid $600+ in interest. Download them as a single ZIP.',
  },
]

const FAQS = [
  {
    q: 'Will my spreadsheet import?',
    a: 'Yes. CSV and XLSX files are supported. LendSolo auto-detects columns (borrower, principal, rate, term, start date) and lets you review the mapping before confirming. Most imports complete in under two minutes.',
  },
  {
    q: 'Do I need to enter every old payment?',
    a: "No. For active loans, enter the current outstanding balance and start tracking new payments — the amortization catches up. If you want historical audit trails, you can backfill payments, but it's optional.",
  },
  {
    q: 'Can I keep my spreadsheet as backup?',
    a: 'Yes. LendSolo never locks your data. Export payments, loans, and statements as CSV any time. Keep your old spreadsheet — it just stops being the source of truth.',
  },
  {
    q: 'What about loans that already paid off?',
    a: 'Closed and paid-off loans can be tracked too. Mark them as paid off and they move to a separate section of your dashboard so your active portfolio stays clean.',
  },
  {
    q: 'Does it generate 1098s automatically?',
    a: 'Any borrower who paid you $600+ in interest during the tax year gets a 1098 auto-generated on the Pro or Fund plan. Download one at a time or all at once as a ZIP.',
  },
  {
    q: 'Can my borrowers log in?',
    a: "Yes. Invite each borrower by email — they get a magic link to a portal that shows their schedule, balance, next payment, and downloadable statements. No passwords for them to manage.",
  },
  {
    q: 'What if I have one investor on one loan?',
    a: "The Fund tier handles that. You record the investor's participation percentage, and every payment automatically splits pro-rata. The investor logs into their portal to see distributions and annual statements.",
  },
  {
    q: 'What does it cost?',
    a: 'Solo $29/mo (up to 5 loans), Pro $49/mo (up to 25 loans + 1098s + AI deal memos), Fund $99/mo (unlimited + investor portal + deal room). Annual billing saves two months.',
  },
]

const SCHEMA = {
  '@graph': [
    {
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@type': 'FinancialProduct',
      name: 'LendSolo',
      description:
        'Loan management software that replaces private-lender spreadsheets. CSV import, automated reminders, 1098 generation.',
      url: 'https://lendsolo.com/when-spreadsheets-break',
      feesAndCommissionsSpecification: 'Solo $29/mo · Pro $49/mo · Fund $99/mo',
      provider: { '@type': 'Organization', name: 'LendSolo', url: 'https://lendsolo.com' },
    },
  ],
}

export default function WhenSpreadsheetsBreak() {
  return (
    <>
      <SeoHead
        title="When Your Loan Spreadsheet Breaks: 5 Signs It's Time — LendSolo"
        description="Tax season chaos? Forgotten payment date? Investor wants a statement? Five signs your loan spreadsheet has hit its limit — and what to do about it."
        canonicalPath="/when-spreadsheets-break"
        schema={SCHEMA}
      />

      <div className="min-h-screen" style={{ backgroundColor: '#f8f7f4' }}>
        <Header />
        <Hero />
        <BreaksSection />
        <MigrationObjectionKiller />
        <FeatureTrio />
        <FounderVoice />
        <PricingStrip />
        <FaqSection />
        <FinalCta />
        <MarketingFooter />
      </div>
    </>
  )
}

function Header() {
  return (
    <header className="bg-white/80 backdrop-blur-sm border-b border-[#e2e0d8] sticky top-0 z-40">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: '#0f1a2e' }}
          >
            <span className="text-[#34d399] font-bold text-sm font-mono">LS</span>
          </div>
          <span className="text-lg font-bold text-[#0f1a2e] font-display">LendSolo</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link
            href="/"
            className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Product
          </Link>
          <Link
            href="/blog"
            className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Blog
          </Link>
          <Link
            href="/users/sign_in"
            className="text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Sign In
          </Link>
          <Link
            href={SIGNUP_HREF}
            className="text-sm font-semibold text-white bg-[#0f1a2e] hover:-translate-y-0.5 hover:shadow-lg transition-all px-4 py-2 rounded-lg"
          >
            Get Started
          </Link>
        </nav>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="relative overflow-hidden pt-20 sm:pt-28 pb-20 sm:pb-24">
      <div
        className="absolute top-[-40%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(26,122,76,0.06) 0%, transparent 70%)',
        }}
      />
      <div className="relative max-w-[1140px] mx-auto px-6 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-5">
          <span className="w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
          The spreadsheet graveyard
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] text-[#0f1a2e] max-w-[820px] mx-auto tracking-tight">
          Your spreadsheet was fine.{' '}
          <em className="italic text-[#1a7a4c]">Until it wasn't.</em>
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[#5a6578] max-w-[620px] mx-auto">
          Most private lenders manage their first 1–2 loans in Excel. It works — right up until the day it doesn't. Here are the five moments that make every micro-lender finally switch.
        </p>
        <div className="mt-10 flex gap-3 justify-center flex-wrap">
          <Link
            href={SIGNUP_HREF}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            Start your free trial
          </Link>
          <a
            href="#tax-season"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-transparent text-[#0f1a2e] rounded-[10px] text-[15px] font-semibold border-[1.5px] border-[#e2e0d8] hover:border-[#5a6578] hover:bg-white transition-all"
          >
            Read the 5 signs ↓
          </a>
        </div>
      </div>
    </section>
  )
}

function BreaksSection() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[820px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          The breaking points
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-12">
          Five moments your spreadsheet fails you.
        </h2>
        <ol className="space-y-12">
          {BREAKS.map((brk) => (
            <li key={brk.id} id={brk.id} className="scroll-mt-20">
              <div className="flex items-baseline gap-4">
                <span className="font-display text-3xl text-[#1a7a4c]">0{brk.n}</span>
                <h3 className="font-display text-2xl font-bold text-[#0f1a2e]">
                  {brk.title}
                </h3>
              </div>
              <p className="mt-3 text-[17px] leading-relaxed text-[#5a6578] max-w-[680px]">
                {brk.body}
              </p>
              <Link
                href={brk.href}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1a7a4c] hover:text-[#15673f]"
              >
                {brk.cta} →
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

function MigrationObjectionKiller() {
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Before you ask
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[820px] mb-12">
          "But moving off the spreadsheet sounds like a hassle."
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {OBJECTIONS.map((o) => (
            <div
              key={o.title}
              className="bg-white border border-[#e2e0d8] rounded-[14px] p-7"
            >
              <h3 className="font-display text-lg font-bold text-[#0f1a2e] mb-2">
                {o.title}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#5a6578]">{o.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FeatureTrio() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          What you get instead
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[780px] mb-12">
          Three things your spreadsheet will never do.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className="bg-[#f8f7f4] border border-[#e2e0d8] rounded-[14px] p-7"
            >
              <div className="font-mono text-xs text-[#1a7a4c] mb-3">0{i + 1}</div>
              <h3 className="font-display text-xl font-bold text-[#0f1a2e] mb-3">
                {f.title}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#5a6578]">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FounderVoice() {
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[820px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Founder
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-6">
          Built by a private lender, not a software company.
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] mb-4">
          I lend in Illinois. Every feature in LendSolo exists because I needed it first — starting with the moment my own spreadsheet stopped being trustworthy.
        </p>
        <p className="text-sm font-semibold text-[#0f1a2e]">— Pete, founder</p>
      </div>
    </section>
  )
}

function PricingStrip() {
  const tiers = [
    { name: 'Solo', price: '$29', note: 'Up to 5 loans' },
    { name: 'Pro', price: '$49', note: 'Up to 25 loans' },
    { name: 'Fund', price: '$99', note: 'Unlimited + investors' },
  ]
  return (
    <section className="py-16 sm:py-20 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <div
          className="border border-[#e2e0d8] rounded-[14px] p-6 sm:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          style={{ backgroundColor: '#f8f7f4' }}
        >
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-2">
              Pricing
            </p>
            <p className="font-display text-xl sm:text-2xl text-[#0f1a2e]">
              Flat monthly pricing.{' '}
              <span className="text-[#5a6578] font-normal">Annual = 2 months free.</span>
            </p>
          </div>
          <div className="flex gap-3 sm:gap-5">
            {tiers.map((t) => (
              <div key={t.name} className="flex flex-col">
                <span className="text-xs font-mono uppercase tracking-wider text-[#8c95a6]">
                  {t.name}
                </span>
                <span className="font-display text-2xl text-[#0f1a2e]">
                  {t.price}
                  <span className="text-sm text-[#5a6578]">/mo</span>
                </span>
                <span className="text-xs text-[#8c95a6]">{t.note}</span>
              </div>
            ))}
          </div>
          <Link
            href="/#pricing"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#0f1a2e] border-[1.5px] border-[#e2e0d8] rounded-lg hover:border-[#5a6578] transition-all"
          >
            Full pricing →
          </Link>
        </div>
      </div>
    </section>
  )
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[720px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          FAQ
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-10">
          Common questions
        </h2>
        <div className="divide-y divide-[#e2e0d8]">
          {FAQS.map((item, i) => {
            const isOpen = openIndex === i
            return (
              <div key={item.q} className="py-6">
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between text-left"
                >
                  <span className="text-base font-semibold text-[#0f1a2e] pr-4">
                    {item.q}
                  </span>
                  <span className="text-xl font-light text-[#8c95a6] min-w-[24px] text-center">
                    {isOpen ? '−' : '+'}
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-[500px] pt-4 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="text-[15px] leading-relaxed text-[#5a6578]">{item.a}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function FinalCta() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8 text-center">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Ready?
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[720px] mx-auto mb-4">
          Try it free for 14 days. Import your spreadsheet. See if it sticks.
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] max-w-[600px] mx-auto mb-9">
          No credit card. Cancel in one click. Export your data any time.
        </p>
        <Link
          href={SIGNUP_HREF}
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
        >
          Start your free trial
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8h10m0 0L9 4m4 4L9 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </div>
    </section>
  )
}
